<template>
  <section id="gallery" class="container mx-auto px-4 py-10 md:py-16">
    <div class="max-w-3xl mx-auto mb-8 md:mb-16 text-center">
      <h2 class="text-3xl md:text-4xl font-bold text-navy mb-6">{{ t('gallery.title') }}</h2>
      <div class="w-24 h-1 bg-leaf mx-auto mb-6 rounded-full"></div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      <button
        v-for="(image, index) in imageGallery"
        :key="image.id"
        class="group relative overflow-hidden rounded-lg shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-leaf"
        @click="openViewer(index)"
      >
        <img
          :src="image.thumb"
          :srcset="`${image.thumb} 400w, ${image.thumb2x} 800w`"
          sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, (min-width: 640px) 50vw, 100vw"
          :alt="t('gallery.imageAlt')"
          width="400"
          height="192"
          loading="lazy"
          decoding="async"
          class="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div class="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 flex items-center justify-center">
          <ZoomIn class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 size-8 drop-shadow" />
        </div>
      </button>
    </div>

    <!-- Lightbox viewer (client-only: teleport + transition are not server-rendered) -->
    <Teleport v-if="isMounted" to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          @click.self="isOpen = false"
        >
          <!-- Image wrapper -->
          <div class="relative flex items-center justify-center">
            <img
              :src="currentImage.image"
              :alt="t('gallery.imageAlt')"
              class="rounded-lg select-none block"
              style="max-width: 90vw; max-height: 90vh; width: auto; height: auto;"
            />

            <!-- Prev button -->
            <button
              class="absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer text-white bg-black/40 hover:bg-black/70 rounded-full p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Previous image"
              @click.stop="prev"
            >
              <ChevronLeft class="size-7" />
            </button>

            <!-- Next button -->
            <button
              class="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-white bg-black/40 hover:bg-black/70 rounded-full p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Next image"
              @click.stop="next"
            >
              <ChevronRight class="size-7" />
            </button>

            <!-- Close button -->
            <button
              class="absolute top-3 right-3 cursor-pointer text-white bg-black/40 hover:bg-black/70 rounded-full p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Close viewer"
              @click="isOpen = false"
            >
              <X class="size-5" />
            </button>

            <!-- Counter -->
            <div class="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/90 text-sm tabular-nums whitespace-nowrap bg-black/40 rounded-full px-3 py-1">
              {{ selectedIndex + 1 }} / {{ imageGallery.length }}
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { onKeyStroke } from "@vueuse/core";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-vue-next";
import { useI18n } from "vue-i18n";

import borowka from "@/assets/borowka.avif";
import borowka1 from "@/assets/borowka1.avif";
import borowka2 from "@/assets/borowka2.avif";
import borowka3 from "@/assets/borowka3.avif";
import borowka4 from "@/assets/borowka4.avif";
import borowka5 from "@/assets/borowka5.avif";
import borowka6 from "@/assets/borowka6.avif";
import borowka7 from "@/assets/borowka7.avif";
import borowka8 from "@/assets/borowka8.avif";
import borowka9 from "@/assets/borowka9.avif";
import borowka10 from "@/assets/borowka10.avif";
import borowka11 from "@/assets/borowka11.avif";
import borowkaThumb from "@/assets/gallery-thumbs/borowka-400.avif";
import borowkaThumb2x from "@/assets/gallery-thumbs/borowka-800.avif";
import borowka1Thumb from "@/assets/gallery-thumbs/borowka1-400.avif";
import borowka1Thumb2x from "@/assets/gallery-thumbs/borowka1-800.avif";
import borowka2Thumb from "@/assets/gallery-thumbs/borowka2-400.avif";
import borowka2Thumb2x from "@/assets/gallery-thumbs/borowka2-800.avif";
import borowka3Thumb from "@/assets/gallery-thumbs/borowka3-400.avif";
import borowka3Thumb2x from "@/assets/gallery-thumbs/borowka3-800.avif";
import borowka4Thumb from "@/assets/gallery-thumbs/borowka4-400.avif";
import borowka4Thumb2x from "@/assets/gallery-thumbs/borowka4-800.avif";
import borowka5Thumb from "@/assets/gallery-thumbs/borowka5-400.avif";
import borowka5Thumb2x from "@/assets/gallery-thumbs/borowka5-800.avif";
import borowka6Thumb from "@/assets/gallery-thumbs/borowka6-400.avif";
import borowka6Thumb2x from "@/assets/gallery-thumbs/borowka6-800.avif";
import borowka7Thumb from "@/assets/gallery-thumbs/borowka7-400.avif";
import borowka7Thumb2x from "@/assets/gallery-thumbs/borowka7-800.avif";
import borowka8Thumb from "@/assets/gallery-thumbs/borowka8-400.avif";
import borowka8Thumb2x from "@/assets/gallery-thumbs/borowka8-800.avif";
import borowka9Thumb from "@/assets/gallery-thumbs/borowka9-400.avif";
import borowka9Thumb2x from "@/assets/gallery-thumbs/borowka9-800.avif";
import borowka10Thumb from "@/assets/gallery-thumbs/borowka10-400.avif";
import borowka10Thumb2x from "@/assets/gallery-thumbs/borowka10-800.avif";
import borowka11Thumb from "@/assets/gallery-thumbs/borowka11-400.avif";
import borowka11Thumb2x from "@/assets/gallery-thumbs/borowka11-800.avif";

const { t } = useI18n();

const imageGallery = [
  { id: 0, image: borowka, thumb: borowkaThumb, thumb2x: borowkaThumb2x },
  { id: 1, image: borowka1, thumb: borowka1Thumb, thumb2x: borowka1Thumb2x },
  { id: 2, image: borowka2, thumb: borowka2Thumb, thumb2x: borowka2Thumb2x },
  { id: 3, image: borowka3, thumb: borowka3Thumb, thumb2x: borowka3Thumb2x },
  { id: 4, image: borowka4, thumb: borowka4Thumb, thumb2x: borowka4Thumb2x },
  { id: 5, image: borowka5, thumb: borowka5Thumb, thumb2x: borowka5Thumb2x },
  { id: 6, image: borowka6, thumb: borowka6Thumb, thumb2x: borowka6Thumb2x },
  { id: 7, image: borowka7, thumb: borowka7Thumb, thumb2x: borowka7Thumb2x },
  { id: 8, image: borowka8, thumb: borowka8Thumb, thumb2x: borowka8Thumb2x },
  { id: 9, image: borowka9, thumb: borowka9Thumb, thumb2x: borowka9Thumb2x },
  { id: 10, image: borowka10, thumb: borowka10Thumb, thumb2x: borowka10Thumb2x },
  { id: 11, image: borowka11, thumb: borowka11Thumb, thumb2x: borowka11Thumb2x },
];

const isMounted = ref(false);
const isOpen = ref(false);
const selectedIndex = ref(0);

onMounted(() => {
  isMounted.value = true;
});

const currentImage = computed(() => imageGallery[selectedIndex.value]);

function openViewer(index: number) {
  selectedIndex.value = index;
  isOpen.value = true;
}

function prev() {
  selectedIndex.value =
    (selectedIndex.value - 1 + imageGallery.length) % imageGallery.length;
}

function next() {
  selectedIndex.value = (selectedIndex.value + 1) % imageGallery.length;
}

onKeyStroke("Escape", () => {
  if (isOpen.value) isOpen.value = false;
});

onKeyStroke("ArrowLeft", (e) => {
  if (isOpen.value) {
    e.preventDefault();
    prev();
  }
});

onKeyStroke("ArrowRight", (e) => {
  if (isOpen.value) {
    e.preventDefault();
    next();
  }
});
</script>
